import i18n from '../i18n'
import { translateText } from './translationService'

/**
 * Extract error message from various error formats
 * @param error - The error object
 * @returns The extracted error message
 */
export const extractErrorMessage = (error: any): string => {
  // Check if error has response.data.message (Axios error format)
  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  // Check if error has response.data.error
  if (error?.response?.data?.error) {
    return error.response.data.error
  }

  // Check if error has data.message
  if (error?.data?.message) {
    return error.data.message
  }

  // Check if error has message
  if (error?.message) {
    return error.message
  }

  // Check if error is a string
  if (typeof error === 'string') {
    return error
  }

  // Default fallback
  return i18n.language === 'fr'
    ? "Une erreur inattendue s'est produite"
    : 'An unexpected error occurred'
}

/**
 * Dynamically translate error message using Google Translate API
 * Backend returns errors in English only, we translate them on the frontend
 * @param message - The English error message from backend
 * @returns Promise with the translated message
 */
export const translateError = async (message: string): Promise<string> => {
  // First, try to find a predefined translation key (for common errors)
  const predefinedTranslation = i18n.t(`errors.${message}`, {
    defaultValue: null,
  })

  // If a predefined translation exists, use it
  if (
    predefinedTranslation !== null &&
    predefinedTranslation !== `errors.${message}`
  ) {
    return predefinedTranslation
  }

  // Get current language
  const currentLanguage = i18n.language || 'en'

  // For dynamic backend errors (always in English), translate to current language
  try {
    const translated = await translateText(message, currentLanguage)
    return translated
  } catch (error) {
    console.error('Error translating message:', error)
    // If translation fails, return original message
    return message
  }
}

/**
 * Handle error and return dynamically translated message
 * @param error - The error object
 * @returns Promise with the translated error message
 */
export const handleError = async (error: any): Promise<string> => {
  const errorMessage = extractErrorMessage(error)
  return await translateError(errorMessage)
}
